import { supabase } from './supabaseClient';
import { MaintenanceRow, rowFromRpc } from './maintenanceLogic';
import { TOR_NOTSCHALTER_UNLESBAR, TorNotschalterStand, torNotschalterAusZeile } from './torNotschalter';

/**
 * Duenne Schicht ueber die RPCs aus supabase/app_maintenance_rpc.sql (Nr. 91)
 * und app_maintenance_fenster_rpc.sql (Nr. 96: Fensterlaenge, Deploy-
 * Erkennung) sowie den Build-Riegel (Nr. 112/113: `min_build`,
 * `maintenance_set_min_build`). Hier steht ausschliesslich das Reden mit der
 * Datenbank; jede Regel — Phase, Countdown, Admin-Umgehung — steht in
 * maintenanceLogic.ts und ist dort ohne Netz testbar.
 *
 * ─── Lesen wirft NIE ───────────────────────────────────────────────────────
 * Die Bauphase ist eine Ansage, kein Schutz: der Riegel sitzt im Trigger auf
 * `projects`. Ein Lesefehler — offline, Migration Nr. 90 nicht gefahren
 * (PostgREST antwortet mit `PGRST202`, die Tabelle fehlt mit `42P01`) — darf
 * die App deshalb weder aufhalten noch aussperren. Er landet als
 * `console.warn` im Protokoll und als „keine Zeile bekannt" beim Aufrufer.
 *
 * Die beiden Admin-Aufrufe werfen dagegen: wer eine Wartung ankuendigt und
 * dabei scheitert, muss das sehen, sonst glaubt er, alle Clients zaehlten
 * bereits herunter.
 */

export interface MaintenanceStatus {
    /** Die geltende Zeile; null = keine bekannt (auch: nicht lesbar). */
    row: MaintenanceRow | null;
    /** `server_now` der RPC in ms — fuer clockOffsetMs; null, wenn nicht lesbar. */
    serverNowMs: number | null;
}

const NOT_AVAILABLE: MaintenanceStatus = { row: null, serverNowMs: null };

/**
 * Ein einziges Mal warnen, dann Ruhe.
 *
 * Der Abgleich laeuft alle 30 s und bei jedem Sichtbarwerden des Tabs. Ist die
 * Migration noch nicht gefahren, schriebe jede Runde dieselbe Warnung in die
 * Konsole und begruebe darunter alles, was dort wirklich zu sehen sein soll.
 */
let readFailureLogged = false;

const serverNowFrom = (payload: unknown): number | null => {
    const raw = payload && typeof payload === 'object'
        ? (payload as Record<string, unknown>).server_now
        : null;
    if (typeof raw !== 'string') return null;
    const ms = Date.parse(raw);
    return Number.isFinite(ms) ? ms : null;
};

/** Erste Zeile einer RPC-Antwort — egal ob Array oder Einzelobjekt. */
const firstRow = (data: unknown): unknown => (Array.isArray(data) ? data[0] : data);

/**
 * Der neue Mindest-Build aus der Antwort von `maintenance_set_min_build`.
 *
 * Eine skalare RPC liefert den timestamptz als blanken String, eine, die
 * eine Zeile zurueckgibt, ein Objekt mit `min_build`. Beide Formen werden
 * gelesen; alles andere — und der leere String — ergibt null, also „Riegel
 * aus". Ein Wert, den diese Funktion nicht versteht, darf nicht als
 * gesetzter Riegel durchgehen.
 */
export const minBuildFrom = (data: unknown): string | null => {
    const v = firstRow(data);
    if (typeof v === 'string') return v !== '' ? v : null;
    if (v && typeof v === 'object') {
        const raw = (v as Record<string, unknown>).min_build;
        return typeof raw === 'string' && raw !== '' ? raw : null;
    }
    return null;
};

export const fetchMaintenance = async (): Promise<MaintenanceStatus> => {
    try {
        const { data, error } = await supabase.rpc('maintenance_status');
        if (error) {
            if (!readFailureLogged) {
                readFailureLogged = true;
                console.warn('[maintenance] Bauphase nicht lesbar — Anzeige bleibt aus:', error);
            }
            return NOT_AVAILABLE;
        }
        const payload = firstRow(data);
        return { row: rowFromRpc(payload), serverNowMs: serverNowFrom(payload) };
    } catch (e) {
        if (!readFailureLogged) {
            readFailureLogged = true;
            console.warn('[maintenance] Bauphase nicht lesbar — Anzeige bleibt aus:', e);
        }
        return NOT_AVAILABLE;
    }
};

/**
 * Kuendigt die Bauphase an (nur Admin; die Pruefung steht in der RPC).
 *
 * Wirft bei jedem Fehler — auch bei einer Antwort, die keine Zeile ist: die
 * Admin-Oberflaeche zeigt den Fehler, statt still einen Countdown zu behaupten.
 */
export const startMaintenance = async (args: {
    /** Vorlauf bis zum Zusperren (1..120). */
    minutes: number;
    message: string;
    /** Fensterlaenge nach dem Zusperren (5..480); danach oeffnet die App von selbst. */
    durationMinutes: number;
    /** Build, der beim Ankuendigen ausgeliefert ist — siehe deployedBuildForStart. */
    deployedBuild: string | null;
}): Promise<MaintenanceRow> => {
    const { data, error } = await supabase.rpc('maintenance_start', {
        p_minutes: args.minutes,
        p_message: args.message,
        p_duration_minutes: args.durationMinutes,
        p_deployed_build: args.deployedBuild,
    });
    if (error) throw error;
    const row = rowFromRpc(firstRow(data));
    if (!row) throw new Error('maintenance_start lieferte keine Bauphasen-Zeile');
    return row;
};

/**
 * Liest den gerade AUSGELIEFERTEN Build-Zeitstempel aus /version.json.
 *
 * Dieselbe Datei, die das Version-Gate abfragt (vite.config.ts legt sie mit
 * `buildTime` ab), ohne Cache. null bei jedem Fehler — der Aufrufer nimmt
 * dann den eigenen Build (deployedBuildForStart), die Bauphase startet
 * trotzdem. Nur der Admin-Klick ruft das, nicht die Flotte.
 */
export const fetchDeployedBuildTime = async (): Promise<string | null> => {
    try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return null;
        const data: unknown = await res.json();
        const raw = data && typeof data === 'object' ? (data as Record<string, unknown>).buildTime : null;
        return typeof raw === 'string' && raw !== '' ? raw : null;
    } catch {
        return null;
    }
};

/**
 * „Das Update ist da" — beendet die Bauphase, wenn `ownBuild` neuer ist als
 * der beim Ankuendigen ausgelieferte Build (Nr. 96, jeder Angemeldete).
 *
 * Wirft NICHT: der Aufruf kommt aus dem Hook beim Laden der Flotte; ein
 * Fehler (Nr. 96 noch nicht gefahren → PGRST202, offline) darf weder den
 * Countdown noch die Wartungsseite stoeren. Die Bauphase endet dann eben
 * per Klick oder mit dem Fenster. Rueckgabe: die neue Zeile, oder null.
 */
export const releaseMaintenanceForBuild = async (ownBuild: string): Promise<MaintenanceRow | null> => {
    try {
        const { data, error } = await supabase.rpc('maintenance_release', { p_build: ownBuild });
        if (error) {
            console.warn('[maintenance] Deploy-Meldung fehlgeschlagen — Bauphase endet per Klick oder Fenster:', error);
            return null;
        }
        return rowFromRpc(firstRow(data));
    } catch (e) {
        console.warn('[maintenance] Deploy-Meldung fehlgeschlagen — Bauphase endet per Klick oder Fenster:', e);
        return null;
    }
};

/**
 * Setzt den Mindest-Build des Riegels — oder hebt ihn auf (`null`).
 *
 * Nur Admin; geprueft wird das in der RPC (Nr. 113). Der Server weist
 * ausserdem zwei Werte ab, die niemand will:
 *   * einen Build, der NEUER ist als der Stempel des Aufrufers selbst —
 *     sonst sperrte sich der Administrator mit dem Klick als Erster aus;
 *   * einen Build mehr als 15 Minuten in der Zukunft.
 *
 * WIRFT — wie `maintenance_start` und `maintenance_end` und aus demselben
 * Grund: wer einen Riegel setzt und dabei scheitert, muss das sehen, sonst
 * haelt er die Flotte fuer gesichert. Bis Nr. 113 gelaufen ist, antwortet
 * PostgREST mit `PGRST202` (Funktion unbekannt) — dieser Fehler landet
 * sichtbar in der Admin-Karte, er darf nichts abstuerzen lassen.
 *
 * Rueckgabe: der neue Wert, so wie der Server ihn fuehrt; null = Riegel aus.
 */
export const setMinBuild = async (build: string | null): Promise<string | null> => {
    const { data, error } = await supabase.rpc('maintenance_set_min_build', { p_build: build });
    if (error) throw error;
    return minBuildFrom(data);
};

/**
 * Beendet die Bauphase (nur Admin).
 *
 * Wirft bei Fehler; eine erfolgreiche Antwort ohne verwertbare Zeile ergibt
 * null — der Hook gleicht ohnehin gleich neu ab.
 */
export const endMaintenance = async (): Promise<MaintenanceRow | null> => {
    const { data, error } = await supabase.rpc('maintenance_end');
    if (error) throw error;
    return rowFromRpc(firstRow(data));
};

// ─── Tor-Notschalter (S5a Stufe 3b, Nr. 177) ────────────────────────────────

/** Ein einziges Mal warnen — die Ausstellung fragt bei jedem Beleg. */
let torReadFailureLogged = false;

/**
 * Der Stand des Tor-Notschalters — FRISCH aus der einen Zeile von
 * `app_maintenance`, ohne Cache (gelesen je Ausstellung, issueInvoice).
 *
 * Wirft NIE und faellt bei jedem Zweifel STRENG aus: Lesefehler, keine Zeile,
 * Spalte fehlt (Nr. 177 nicht gefahren, PostgREST antwortet mit 42703) —
 * dann gilt der Journal-Modus (torNotschalter.ts, „Unbekannt heisst
 * STRENG"). Eine Ausstellung darf an diesem Lesen nicht scheitern, und ein
 * Lesefehler darf das Tor nicht still oeffnen.
 */
export const fetchTorNotschalter = async (): Promise<TorNotschalterStand> => {
    try {
        const { data, error } = await supabase
            .from('app_maintenance')
            .select('tor_json_seit, tor_json_von')
            .eq('id', true)
            .maybeSingle();
        if (error) {
            if (!torReadFailureLogged) {
                torReadFailureLogged = true;
                console.warn('[maintenance] Tor-Notschalter nicht lesbar — es gilt der Journal-Modus:', error);
            }
            return TOR_NOTSCHALTER_UNLESBAR;
        }
        return torNotschalterAusZeile(data);
    } catch (e) {
        if (!torReadFailureLogged) {
            torReadFailureLogged = true;
            console.warn('[maintenance] Tor-Notschalter nicht lesbar — es gilt der Journal-Modus:', e);
        }
        return TOR_NOTSCHALTER_UNLESBAR;
    }
};

/**
 * Legt den Tor-Notschalter um (nur Admin; geprueft in der RPC Nr. 177).
 * `an = true` heisst: Zuordnung aus dem JSON, das Tor sperrt nicht.
 *
 * WIRFT — wie `maintenance_set_min_build` und aus demselben Grund: wer den
 * Schalter umlegt und dabei scheitert, muss das sehen. Bis Nr. 177 gelaufen
 * ist, antwortet PostgREST mit `PGRST202`. Eine Antwort ohne lesbaren Stand
 * gilt ebenfalls als Fehlschlag — die Karte liest danach ohnehin neu.
 */
export const setTorNotschalter = async (an: boolean): Promise<TorNotschalterStand> => {
    const { data, error } = await supabase.rpc('maintenance_set_tor_notschalter', { p_json: an });
    if (error) throw error;
    const stand = torNotschalterAusZeile(data);
    if (!stand.lesbar) throw new Error('maintenance_set_tor_notschalter lieferte keinen lesbaren Stand');
    return stand;
};
